// First-party cookie/consent state — no third-party CMP, no recurring
// licence cost. This is the single source of truth for what the visitor
// has agreed to; the banner (components/CookieConsent.jsx) reads/writes
// through here.
//
// The `analytics` category gates Google Analytics (lib/analytics.js), which
// reads this module's record and listens for CONSENT_CHANGE_EVENT. Until a
// visitor accepts, GA runs in Consent Mode with analytics_storage denied
// (cookieless pings only, nothing stored on the device).
//
// UK PECR posture: STRICT OPT-IN. Nothing non-essential is stored on the
// device until the visitor explicitly consents. The only thing we persist
// before a choice is the consent record itself — which is "strictly
// necessary" (it remembers the visitor's lawful preference), so storing it
// needs no consent. The analytics session id is NEVER written until
// `analytics` consent is granted, and is actively cleared on withdrawal.
//
// Categories today:
//   • necessary — always on, no toggle (auth/session + this consent record).
//   • analytics — first-party page/duration metrics. Default OFF.
// Add a category here + a row in the banner's manage panel to extend it.

const CONSENT_KEY = 'tempdash.consent'

// Bump when the cookie/tracking behaviour materially changes (new category,
// new processor). A stored record from an older version is treated as "no
// decision" so the banner re-prompts and we re-collect lawful consent.
export const CONSENT_VERSION = 1

// The (V1) analytics tracker's session-id key. Nothing mints this id any
// more, but withdrawal still purges it so a value left on a returning
// visitor's device from the V1 site is cleaned up.
export const ANALYTICS_SESSION_KEY = 'tempdash.analytics.sessionId'

// Fired on the window whenever the stored decision changes, so a listener
// can start/stop tracking live without a page reload.
export const CONSENT_CHANGE_EVENT = 'tempdash:consent-change'
// Fired to ask the banner to (re)open its manage panel — e.g. the footer's
// "Cookie settings" link, reachable even after a decision was made
// (withdrawal is as easy as granting, per UK GDPR).
export const CONSENT_OPEN_EVENT = 'tempdash:open-consent'

// Read + validate the stored decision. Returns null when there's no record,
// it's unparseable, or it predates the current CONSENT_VERSION (→ re-prompt).
export function getConsent() {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || parsed.v !== CONSENT_VERSION) return null
    return parsed
  } catch {
    return null
  }
}

// Has the visitor made a (current-version) choice at all? Drives whether the
// banner shows on load.
export function hasDecision() {
  return getConsent() !== null
}

export function hasAnalyticsConsent() {
  return getConsent()?.analytics === true
}

// Persist a decision and broadcast it. `prefs.analytics` is coerced to a
// strict boolean so an absent category reads as "denied", never "undefined".
export function setConsent(prefs) {
  const analytics = prefs?.analytics === true
  const record = {
    v: CONSENT_VERSION,
    analytics,
    // ISO timestamp of the decision — our auditable proof of when/what was
    // agreed. Kept simple (string) so the record is human-readable in
    // devtools and survives JSON round-trips.
    ts: new Date().toISOString(),
  }
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(record))
  } catch {
    /* localStorage blocked (private mode / disabled) — consent simply won't
       persist, so the banner reappears next load and analytics stays off.
       That's the safe direction to fail. */
  }
  // On withdrawal (or reject), purge any analytics session id we may have
  // minted while consent was live. Leaving it would keep an identifier on
  // the device after the lawful basis was removed.
  if (!analytics) {
    try { window.localStorage.removeItem(ANALYTICS_SESSION_KEY) } catch { /* ignore */ }
  }
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT))
  } catch { /* CustomEvent unsupported — extremely old browser; ignore */ }
  return record
}

// Convenience helpers for the banner's primary buttons.
export function acceptAll() {
  return setConsent({ analytics: true })
}

export function rejectAll() {
  return setConsent({ analytics: false })
}

// Ask the banner to open its manage panel (used by the footer link).
export function openConsentManager() {
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT))
  } catch { /* ignore */ }
}
